<template>
  <BaseModal
    id="edit-category-modal"
    class="edit-category-modal"
    v-model="isOpenModal"
  >
    <FileSelector
      v-model="showFileSelector"
      type="assets"
      subcategory="image_category"
      @fileUploaded="fileUploaded"
      @selectedFile="fileUploaded"
    />
    <template v-slot:header v-if="category">
      {{ category.categoryName }}
    </template>
    <div class="navigation">
      <div class="bar">
        <button
          @click="selectedSection = 'details'"
          class="tablinks"
          :class="{ active: selectedSection == 'details' }"
        >
          Details
        </button>
        <button
          v-if="category.id != null"
          @click="selectedSection = 'subcategories'"
          class="tablinks"
          :class="{ active: selectedSection == 'subcategories' }"
        >
          Subcategories
          <span v-if="subCategoriesLoaded" class="tab-count">
            {{ subCategories.length }}
          </span>
          <span
            v-if="missingSubcategoryImages"
            class="tab-count warning"
            :title="`${missingSubcategoryImages} without image`"
          >
            {{ missingSubcategoryImages }} without image
          </span>
        </button>
      </div>
    </div>
    <div
      v-if="selectedSection == 'subcategories'"
      class="content-subcategory-edit"
    >
      <SubCategoryManager
        :subCategories="subCategories"
        :categoryId="category.id"
        @createdSubcategory="createdSubcategory"
        @updatedSubcategory="updatedSubcategory"
        @subcategoryDeleted="subcategoryDeleted"
      />
    </div>
    <div class="content-category-edit" v-if="selectedSection == 'details'">
      <template v-if="!loadingSaving">
        <div v-if="categoryToUpdate" class="details-layout">
          <div class="image-panel">
            <div
              class="preview"
              :class="{ empty: !showImage(categoryToUpdate.image) }"
              @click="showFileSelector = true"
            >
              <img
                v-if="showImage(categoryToUpdate.image)"
                :src="categoryToUpdate.image"
                :alt="categoryToUpdate.categoryName"
                @error="brokenImage = categoryToUpdate.image"
              />
              <div v-else class="placeholder">
                <img src="@/assets/icons/upload-clear.svg" alt="" />
                <span>
                  {{
                    hasImage(categoryToUpdate.image)
                      ? "Image not found, upload a new one"
                      : "This category has no image. Click to upload one."
                  }}
                </span>
              </div>
            </div>
            <div class="image-actions">
              <button class="link-btn" @click="showFileSelector = true">
                {{
                  hasImage(categoryToUpdate.image)
                    ? "Change image"
                    : "Upload image"
                }}
              </button>
              <button
                v-if="hasImage(categoryToUpdate.image)"
                class="link-btn danger"
                @click="deleteCategoryImage()"
              >
                Remove image
              </button>
            </div>
          </div>
          <div class="form-panel">
            <div class="input-item">
              <label for="category-name">Category Name</label>
              <input
                id="category-name"
                type="text"
                class="regular clear"
                v-model="categoryToUpdate.categoryName"
              />
            </div>
            <div class="input-item">
              <label for="category-url">Category URL</label>
              <input
                id="category-url"
                type="text"
                class="regular clear"
                @keyup="cleanCategoryUrl()"
                v-model="categoryToUpdate.categoryUrl"
              />
            </div>
            <div class="input-item">
              <label for="status">Status</label>
              <BaseSwitchCustom id="status" v-model="categoryToUpdate.status" />
            </div>
            <div class="actions" v-if="changesPending">
              <button @click="discardChanges()" class="btn-dark-button">
                Discard changes
              </button>
              <button @click="confirmChanges()" class="btn-success-button">
                Confirm Changes
              </button>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <BaseSmallSpinner />
      </template>
    </div>
  </BaseModal>
</template>
<script>
import FileSelector from "@/components/FileSelector.vue";
import SubCategoryManager from "@/components/SubCategoryManager.vue";
import {
  updateCategory,
  getSubcategories,
  hasImage
} from "@/api/store/CategoriesService.js";

export default {
  components: {
    FileSelector,
    SubCategoryManager
  },
  data() {
    return {
      isOpenModal: true,
      categoryToUpdate: null,
      selectedSection: "details",
      showFileSelector: false,
      subCategories: [],
      subCategoriesLoaded: false,
      brokenImage: null,
      loadingSaving: false
    };
  },
  props: {
    category: {
      type: Object,
      required: true
    },
    initialSection: {
      type: String,
      default: "details"
    }
  },
  computed: {
    missingSubcategoryImages() {
      return this.subCategories.filter(s => !hasImage(s.subCategoryImage))
        .length;
    },
    changesPending() {
      return !(
        JSON.stringify(this.categoryToUpdate) == JSON.stringify(this.category)
      );
    }
  },
  watch: {
    isOpenModal(val) {
      if (!val) {
        this.$emit("closedCategoryEdition");
      } else {
        this.subCategories = [];
      }
    },
    category(val) {
      this.categoryToUpdate = { ...val };
    },
    selectedSection(val) {
      if (val == "subcategories") {
        this.loadSubcategories();
      }
    }
  },
  mounted() {
    this.categoryToUpdate = { ...this.category };
    if (this.category.id != null) {
      // El watcher de selectedSection carga las subcategorías al abrir esa pestaña.
      if (this.initialSection == "subcategories") {
        this.selectedSection = this.initialSection;
      } else {
        this.loadSubcategories();
      }
    }
  },
  methods: {
    hasImage,
    showImage(url) {
      return hasImage(url) && url != this.brokenImage;
    },
    cleanCategoryUrl() {
      this.categoryToUpdate.categoryUrl = this.categoryToUpdate.categoryUrl
        .replace(/[ -]/g, "_")
        .replace(/\W/g, "")
        .toLowerCase();
    },
    createdSubcategory(subCategory) {
      this.subCategories.unshift(subCategory);
    },
    updatedSubcategory(subCategory) {
      const i = this.subCategories.findIndex(x => {
        return x.id == subCategory.id;
      });
      this.subCategories.splice(i, 1, { ...subCategory });
    },
    async loadSubcategories() {
      this.subCategories = await getSubcategories(this.category.id);
      this.subCategoriesLoaded = true;
    },
    async deleteCategoryImage() {
      this.categoryToUpdate.image = null;
    },
    fileUploaded(file) {
      this.categoryToUpdate.image = file.s3Url;
    },
    discardChanges() {
      this.categoryToUpdate = { ...this.category };
    },
    async confirmChanges() {
      let res = await updateCategory(this.category.id, this.categoryToUpdate);
      if (res.status == "success") {
        let message;
        if (this.category.id == null) {
          this.$emit("createdCategory", res.category);
          message = "category created";
        } else {
          this.$emit("updatedCategory", res.category);
          message = "category updated";
        }
        this.$toast.open({
          message: message,
          type: "success",
          position: "top-right"
        });
      } else {
        this.$toast.open({
          message: `Error updating category`,
          type: "error",
          position: "top-right"
        });
      }
    },
    subcategoryDeleted(subcategory) {
      this.subCategories = this.subCategories.filter(x => {
        return x.id != subcategory.id;
      });
    }
  }
};
</script>
<style lang="scss">
#edit-category-modal.modal {
  padding-top: 50px;
}
#edit-category-modal .modal-content {
  height: 95%;
}
</style>
<style lang="scss" scoped>
.tab-count {
  display: inline-block;
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 7px;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.85);
  color: #374151;
  &.warning {
    background-color: #fff4e5;
    color: #a15c00;
  }
}
.content-category-edit {
  padding: 24px 30px !important;
  max-width: 960px;
  margin: 0 auto;
  .details-layout {
    display: grid;
    grid-template-columns: minmax(260px, 420px) 1fr;
    gap: 32px;
    align-items: start;
    @media screen and (max-width: 800px) {
      grid-template-columns: 1fr;
    }
  }
  .image-panel {
    display: flex;
    flex-direction: column;
    gap: 10px;
    .preview {
      aspect-ratio: 185 / 120;
      border-radius: 10px;
      overflow: hidden;
      border: 1px solid #e5e7eb;
      background-color: #f3f4f6;
      cursor: pointer;
      transition: box-shadow 0.15s ease;
      &:hover {
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
      }
      > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      &.empty {
        border: 2px dashed #f5a623;
        background-color: #fff8ee;
      }
      .placeholder {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px;
        text-align: center;
        color: #a15c00;
        font-size: 14px;
        font-weight: 600;
        img {
          width: 44px;
          opacity: 0.7;
        }
      }
    }
    .image-actions {
      display: flex;
      gap: 16px;
    }
  }
  .form-panel {
    display: flex;
    flex-direction: column;
    gap: 14px;
    .input-item {
      display: flex;
      flex-direction: column;
      label {
        margin-bottom: 6px;
        font-size: 13px;
        color: #4b5563;
      }
    }
    .actions {
      display: flex;
      gap: 10px;
      margin-top: 6px;
    }
  }
  .link-btn {
    font-size: 13px;
    font-weight: 600;
    color: #1d4ed8;
    &:hover {
      text-decoration: underline;
    }
    &.danger {
      color: #a61b1b;
    }
  }
}
.content-subcategory-edit {
  width: 100%;
  padding: 20px;
  overflow-y: auto;
}
</style>
